import { rosterDisplayName } from "@repo/vihangam-yog-shared/roster";
import { Mail, Phone, Send, X } from "lucide-react";
import { useId, useMemo, useState } from "react";

import { CHAPTER } from "../chapter";
import { SearchField } from "../components/DataKit";
import { friendlyError } from "../lib/friendlyError";
import { trpc } from "../trpc";

type Status = "new" | "replied" | "archived";
type Filter = Status | "all";

/** One row as returned by adminListInquiries. */
type Inquiry = {
  id: string;
  name: string;
  contact: string;
  message: string | null;
  chapter: string;
  status: string;
  repliedAt: string | null;
  repliedBy: string | null;
  replyBody: string | null;
  createdAt: string;
  updatedAt: string;
  canEmail: boolean;
};

const STATUS_LABEL = {
  new: "Needs a reply",
  replied: "Replied",
  archived: "No reply needed",
} satisfies Record<Status, string>;

function isStatus(value: string): value is Status {
  return value === "new" || value === "replied" || value === "archived";
}

const FILTERS: { id: Filter; label: string }[] = [
  { id: "new", label: STATUS_LABEL.new },
  { id: "replied", label: STATUS_LABEL.replied },
  { id: "archived", label: STATUS_LABEL.archived },
  { id: "all", label: "All" },
];

/** What the page says after a reply or a move; a move can be undone. */
interface Notice {
  text: string;
  undo: { id: string; status: Status } | null;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function firstNameOf(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

/** A phone contact as a dialable number, or null when it isn't one. */
function telHref(contact: string): string | null {
  const digits = contact.replace(/[^\d+]/g, "");
  return digits.replace(/\D/g, "").length >= 6 ? `tel:${digits}` : null;
}

const actionClass =
  "inline-flex min-h-11 items-center gap-2 rounded-full border border-[color:var(--color-line-strong)] px-4 text-label font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)] disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Website messages: what people sent through the contact form on the
 * chapter's site, worked from inside the team app instead of the shared
 * inbox. Each one needs a reply until someone replies here (the email
 * goes to them with the team inbox copied, and it moves to Replied),
 * marks it replied another way, or says no reply is needed.
 *
 * Only messages sent after this shipped are here; earlier ones lived only
 * in email and were never recorded.
 *
 * Auto-refreshes every 30s while focused.
 */
export function Inquiries() {
  const utils = trpc.useUtils();
  const query = trpc.adminListInquiries.useQuery(undefined, {
    retry: false,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });

  const [filter, setFilter] = useState<Filter>("new");
  const [search, setSearch] = useState("");
  // Lives here, not on the card: after a reply or a move the card leaves
  // the current filter, and the confirmation (and its Undo) must not go
  // with it.
  const [notice, setNotice] = useState<Notice | null>(null);

  const undo = trpc.adminUpdateInquiryStatus.useMutation({
    onSuccess: (_data, vars) => {
      void utils.adminListInquiries.invalidate();
      setNotice({
        text: `Moved back to ${STATUS_LABEL[vars.status]}.`,
        undo: null,
      });
    },
    onError: (err) => {
      setNotice((prev) =>
        prev ? { ...prev, text: friendlyError(err) } : prev,
      );
    },
  });

  const all = useMemo<Inquiry[]>(() => query.data ?? [], [query.data]);
  const countOf = (id: Filter) =>
    id === "all" ? all.length : all.filter((i) => i.status === id).length;
  const newCount = countOf("new");

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((i) => {
      if (filter !== "all" && i.status !== filter) {
        return false;
      }
      if (!q) {
        return true;
      }
      return [i.name, i.contact, i.message ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [all, filter, search]);

  const emptyText =
    all.length === 0
      ? "No messages yet."
      : search.trim()
        ? "Nothing matches that search."
        : filter === "new"
          ? "Nothing needs a reply. You're all caught up."
          : "No messages here yet.";

  return (
    <section className="mx-auto max-w-[1100px] px-5 py-10 md:px-10 md:py-12">
      <header className="border-b border-[color:var(--color-line)] pb-6">
        <h1 className="page-title">Messages from the website</h1>
        <p className="mt-3 max-w-2xl text-body text-[color:var(--color-ink-soft)]">
          Messages people sent through the contact form on {CHAPTER.siteHost}.
          When you reply here, they get your message by email and a copy goes to
          the team inbox.
        </p>
      </header>

      {query.isLoading ? (
        <p className="mt-8 text-body text-[color:var(--color-ink-soft)]">
          Loading messages…
        </p>
      ) : null}

      {query.error ? (
        <p className="mt-8 text-body text-[color:var(--color-danger)]">
          Couldn&apos;t load messages.{" "}
          {friendlyError(
            query.error,
            "Check your internet connection and try again.",
          )}
        </p>
      ) : null}

      {query.data ? (
        <>
          <p className="mt-8 text-lead text-[color:var(--color-ink)]">
            {newCount === 0
              ? "Nothing needs a reply. You're all caught up."
              : `${newCount} ${newCount === 1 ? "message needs" : "messages need"} a reply.`}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div
              role="group"
              aria-label="Show messages"
              className="flex flex-wrap gap-2"
            >
              {FILTERS.map((f) => {
                const isActive = filter === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => setFilter(f.id)}
                    className={`min-h-11 rounded-full px-4 text-label font-semibold tabular-nums transition-colors ${
                      isActive
                        ? "bg-[color:var(--color-amber)] text-[color:var(--color-paper)]"
                        : "border border-[color:var(--color-line-strong)] text-[color:var(--color-ink)] hover:border-[color:var(--color-ink)]"
                    }`}
                  >
                    {f.label} ({countOf(f.id)})
                  </button>
                );
              })}
            </div>
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Search by name, email, phone or words in the message"
            />
          </div>

          <div className="mt-6 flex flex-col gap-4">
            {visible.length === 0 ? (
              <p className="empty-state">{emptyText}</p>
            ) : (
              visible.map((inq) => (
                <InquiryCard key={inq.id} inq={inq} onNotice={setNotice} />
              ))
            )}
          </div>
        </>
      ) : null}

      {notice ? (
        <div
          role="status"
          className="fixed inset-x-4 bottom-4 z-40 flex items-center gap-3 rounded-lg border border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] py-2 pr-2 pl-4 shadow-lg md:right-8 md:left-auto md:max-w-md"
          style={{ marginBottom: "env(safe-area-inset-bottom)" }}
        >
          <p className="flex-1 text-body text-[color:var(--color-ink)]">
            {notice.text}
          </p>
          {notice.undo ? (
            <button
              type="button"
              disabled={undo.isPending}
              onClick={() => {
                if (notice.undo) {
                  undo.mutate(notice.undo);
                }
              }}
              className={actionClass}
            >
              {undo.isPending ? "Undoing…" : "Undo"}
            </button>
          ) : null}
          <button
            type="button"
            aria-label="Close this message"
            onClick={() => setNotice(null)}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[color:var(--color-ink-soft)] hover:text-[color:var(--color-ink)]"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
      ) : null}
    </section>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === "new"
      ? {
          background: "color-mix(in srgb, var(--color-amber) 15%, transparent)",
          color: "var(--color-ink)",
        }
      : status === "replied"
        ? {
            background: "color-mix(in srgb, var(--color-ink) 10%, transparent)",
            color: "var(--color-ink)",
          }
        : {
            background: "transparent",
            color: "var(--color-ink-soft)",
            border: "1px solid var(--color-line-strong)",
          };
  return (
    <span className="badge" style={tone}>
      {isStatus(status) ? STATUS_LABEL[status] : status}
    </span>
  );
}

function InquiryCard({
  inq,
  onNotice,
}: {
  inq: Inquiry;
  onNotice: (notice: Notice) => void;
}) {
  const utils = trpc.useUtils();
  const [replying, setReplying] = useState(false);
  const [body, setBody] = useState("");
  const replyFieldId = useId();
  const firstName = firstNameOf(inq.name);

  const reply = trpc.adminReplyToInquiry.useMutation({
    onSuccess: () => {
      void utils.adminListInquiries.invalidate();
      setReplying(false);
      setBody("");
      onNotice({
        text: `Reply sent to ${firstName}. It's under Replied now.`,
        undo: null,
      });
    },
  });
  const setStatus = trpc.adminUpdateInquiryStatus.useMutation({
    onSuccess: (_data, vars) => {
      void utils.adminListInquiries.invalidate();
      onNotice({
        text: `Moved ${firstName} to ${STATUS_LABEL[vars.status]}.`,
        // A sent email can't be recalled, so only a manual move offers Undo.
        undo: isStatus(inq.status) ? { id: inq.id, status: inq.status } : null,
      });
    },
  });

  const busy = reply.isPending || setStatus.isPending;
  const tel = inq.canEmail ? null : telHref(inq.contact);
  const repliedByName = inq.repliedBy
    ? (rosterDisplayName(inq.repliedBy) ?? inq.repliedBy)
    : null;

  return (
    <article className="surface p-5 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="text-title font-bold text-[color:var(--color-ink)]">
              {inq.name}
            </h2>
            <StatusBadge status={inq.status} />
          </div>
          {inq.chapter === "nz" ? (
            <p className="mt-1 text-meta text-[color:var(--color-ink-soft)]">
              From the New Zealand site
            </p>
          ) : null}
          <p className="mt-1 flex items-center gap-2 text-label break-all text-[color:var(--color-ink-soft)]">
            {inq.canEmail ? (
              <Mail size={16} aria-hidden="true" className="shrink-0" />
            ) : (
              <Phone size={16} aria-hidden="true" className="shrink-0" />
            )}
            {inq.canEmail ? (
              <a
                href={`mailto:${inq.contact}`}
                className="underline underline-offset-4 hover:text-[color:var(--color-ink)]"
              >
                {inq.contact}
              </a>
            ) : tel ? (
              <a
                href={tel}
                className="underline underline-offset-4 hover:text-[color:var(--color-ink)]"
              >
                {inq.contact}
              </a>
            ) : (
              <span>{inq.contact}</span>
            )}
          </p>
        </div>
        <p className="text-meta whitespace-nowrap text-[color:var(--color-ink-soft)] tabular-nums">
          {formatDate(inq.createdAt)}
        </p>
      </div>

      {inq.message ? (
        <p className="mt-4 text-body whitespace-pre-wrap text-[color:var(--color-ink)]">
          {inq.message}
        </p>
      ) : (
        <p className="mt-4 text-body text-[color:var(--color-ink-soft)]">
          No message left.
        </p>
      )}

      {/* What was already replied, if anything. */}
      {inq.replyBody ? (
        <div className="mt-4 rounded-r-lg border-l-4 border-[color:var(--color-amber)]/50 bg-[color:var(--color-bg)] px-4 py-3">
          <p className="text-meta font-medium text-[color:var(--color-ink-soft)]">
            Replied
            {repliedByName ? ` by ${repliedByName}` : ""}
            {inq.repliedAt ? ` on ${formatDate(inq.repliedAt)}` : ""}
          </p>
          <p className="mt-1.5 text-body whitespace-pre-wrap text-[color:var(--color-ink)]">
            {inq.replyBody}
          </p>
        </div>
      ) : null}

      {/* Reply composer. */}
      {replying ? (
        <div className="mt-4">
          <label
            htmlFor={replyFieldId}
            className="block text-label font-semibold text-[color:var(--color-ink)]"
          >
            Your reply to {firstName}
          </label>
          <p
            id={`${replyFieldId}-hint`}
            className="mt-1 text-label text-[color:var(--color-ink-soft)]"
          >
            We start the email with &ldquo;Hi {firstName},&rdquo; and sign it
            from the {CHAPTER.name} team, so just write the middle.
          </p>
          <textarea
            id={replyFieldId}
            aria-describedby={`${replyFieldId}-hint`}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            autoFocus
            className="mt-2 w-full resize-y rounded-lg border border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] px-3.5 py-3 text-body text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-amber)]"
          />
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={busy || body.trim().length === 0}
              onClick={() => reply.mutate({ id: inq.id, body: body.trim() })}
              className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[color:var(--color-amber)] px-5 text-label font-semibold text-[color:var(--color-paper)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Send size={16} aria-hidden="true" />
              {reply.isPending ? "Sending…" : "Send reply"}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setReplying(false);
                setBody("");
              }}
              className={actionClass}
            >
              Cancel
            </button>
          </div>
          {reply.error ? (
            <p
              role="alert"
              className="mt-2 text-label text-[color:var(--color-danger)]"
            >
              {friendlyError(reply.error)}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-2.5">
            {inq.canEmail ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => setReplying(true)}
                className={actionClass}
              >
                <Mail size={16} aria-hidden="true" />
                {inq.status === "replied" ? "Reply again" : "Reply by email"}
              </button>
            ) : tel ? (
              <a href={tel} className={actionClass}>
                <Phone size={16} aria-hidden="true" />
                Call {firstName}
              </a>
            ) : null}

            {/* Manual moves. "I've replied another way" covers a phone call. */}
            {inq.status !== "replied" ? (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  setStatus.mutate({ id: inq.id, status: "replied" })
                }
                className={actionClass}
              >
                I&apos;ve replied another way
              </button>
            ) : null}
            {inq.status !== "archived" ? (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  setStatus.mutate({ id: inq.id, status: "archived" })
                }
                className={actionClass}
              >
                {inq.status === "new"
                  ? "No reply needed"
                  : "Move to No reply needed"}
              </button>
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={() => setStatus.mutate({ id: inq.id, status: "new" })}
                className={actionClass}
              >
                Move back to Needs a reply
              </button>
            )}
          </div>
          {setStatus.error ? (
            <p
              role="alert"
              className="mt-2 text-label text-[color:var(--color-danger)]"
            >
              {friendlyError(setStatus.error)}
            </p>
          ) : null}
        </div>
      )}
    </article>
  );
}
